import type { ReadonlyJSONValue } from "@rocicorp/zero";
import { styled } from "goober";
import { CodeBlock } from "../CodeBlock";
import type { ToolResult } from "./ChatTool";

const Panel = styled("div")`
  margin: -12px;
  padding: 12px;
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
`;

const Path = styled("div")`
  font-size: 10px;
  line-height: 16px;
  font-weight: 450;
  color: var(--text-tertiary);
  margin-bottom: 4px;
`;

const Divider = styled("div")`
  border-top: 0.5px solid var(--alpha8);
  margin: 12px -12px;
`;

const Output = styled("pre")`
  margin: 0;
  font-family: var(--font-mono);
  font-size: 10px;
  line-height: 16px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: var(--text-primary);
  &[data-tone="muted"] {
    color: var(--text-tertiary);
  }
  &[data-tone="failure"] {
    color: var(--red-600);
  }
`;

export function jsonRecord(v: ReadonlyJSONValue | undefined) {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, ReadonlyJSONValue>)
    : null;
}

export function jsonString(v: ReadonlyJSONValue | undefined) {
  return typeof v === "string" ? v : undefined;
}

export function rawOutput(result: ToolResult) {
  const text =
    typeof result.output === "string"
      ? result.output
      : JSON.stringify(result.output, null, 2);
  return result.isError ? { failure: text } : { stdout: text };
}

export type CommandSource = { path?: string; language: string; code: string };

export function CommandRun({
  sources,
  stdout,
  stderr,
  notes = [],
  failure,
}: {
  sources: CommandSource[];
  stdout?: string;
  stderr?: string;
  notes?: string[];
  failure?: string;
}) {
  const hasResult = !!(stdout || stderr || notes.length > 0 || failure);
  return (
    <Panel>
      {sources.map((source, i) => (
        <div
          key={source.path ?? ""}
          style={i > 0 ? { marginTop: 12 } : undefined}
        >
          {source.path && <Path>{source.path}</Path>}
          <CodeBlock
            content={source.code}
            language={source.language}
            wrap
            bare
          />
        </div>
      ))}
      {hasResult && (
        <>
          <Divider />
          {stdout && <Output>{stdout}</Output>}
          {stderr && <Output data-tone="muted">{stderr}</Output>}
          {notes.length > 0 && (
            <Output data-tone="muted">{notes.join("\n")}</Output>
          )}
          {failure && <Output data-tone="failure">{failure}</Output>}
        </>
      )}
    </Panel>
  );
}
